import React, { useEffect, useState } from 'react'
import { Box, Card, CardContent, Divider, MenuItem, TextField, Typography } from '@mui/material'

import {
	IDwUsageCosts,
	IDwUsageStatus,
	formatMonth,
	getDwCosts,
} from '../../Services/datawarehouse'
import { DwCostsDetail } from './DwCostsDetail'
import { DwMonthlyChart } from './DwMonthlyChart'

interface Props {
	status: IDwUsageStatus
}

/** Valeur du sélecteur pour la fenêtre par défaut (30 journées couvertes). */
const RECENT = 'recent'

// Coût Google : l'évolution sur douze mois, et le détail de la période choisie.
//
// Le détail d'un mois est servi par une route dédiée plutôt que calculé ici : il
// doit rendre EXACTEMENT la même chose que l'aperçu par défaut, part non
// rattachée et comparaison à la période précédente comprises. Deux calculs
// finiraient par diverger, et l'écran dirait deux choses selon la période
// choisie.

// #region DwCostsSection
export const DwCostsSection: React.FC<Props> = ({ status }) => {
	const [period, setPeriod] = useState(RECENT)
	const [costs, setCosts] = useState<IDwUsageCosts | undefined>(status.costs)
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (period === RECENT) {
			setCosts(status.costs)
			return
		}
		setLoading(true)
		getDwCosts(period)
			.then(setCosts)
			.catch(() => setCosts(undefined))
			.finally(() => setLoading(false))
	}, [period, status.costs])

	// ⚠️ Seuls les mois qui ont au moins une journée mesurée sont proposés :
	// choisir un mois vide n'afficherait rien et se lirait comme une panne de
	// l'écran, alors que c'est l'historique qui ne remonte pas si loin.
	const selectable = status.monthly.filter((m) => m.measuredDays > 0)

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Coût Google
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
					Ce que Quanti paie à Google, tous projets confondus — infrastructure et projets
					internes compris. À ne pas confondre avec ce qui est refacturé aux clients.
				</Typography>

				{status.monthly.length > 0 && (
					<Box sx={{ mb: 2 }}>
						<DwMonthlyChart monthly={status.monthly} />
					</Box>
				)}

				<Divider sx={{ my: 2 }} />

				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
					<TextField
						select
						size="small"
						label="Période détaillée"
						value={period}
						onChange={(e) => setPeriod(e.target.value)}
						sx={{ minWidth: 220 }}
					>
						<MenuItem value={RECENT}>30 dernières journées</MenuItem>
						{/* Du plus récent au plus ancien : c'est l'ordre dans lequel on
						    cherche un mois, l'inverse de celui du graphe. */}
						{[...selectable].reverse().map((m) => (
							<MenuItem key={m.month} value={m.month}>
								{formatMonth(m.month)}
								{m.partial ? ' (partiel)' : ''}
							</MenuItem>
						))}
					</TextField>
					{loading && (
						<Typography variant="caption" color="text.secondary">
							Chargement…
						</Typography>
					)}
				</Box>

				{costs ? (
					<DwCostsDetail costs={costs} />
				) : (
					<Typography variant="caption" color="text.secondary">
						Aucune dépense mesurée sur cette période.
					</Typography>
				)}
			</CardContent>
		</Card>
	)
}

// #endregion
